import { RefObject, useCallback, useEffect, useState } from 'react';

import { DESKTOP_MIN, DESKTOP_TABLET_MIN } from 'settings/device';
import {
  CATEGORY_IMAGE_PRODUCT_HEIGHT,
  CATEGORY_IMAGE_PRODUCT_WIDTH,
} from 'settings/imageSizes';

const TEXT_HEIGHT_TABLET = 91.813; // This is a static number because if the category is empty, we cannot know the height of text.
const TEXT_HEIGHT_DESKTOP = 48.234;

export function useRatioCalculator(
  containerRef: RefObject<HTMLDivElement>
): string | undefined {
  const [ratio, setRatio] = useState<string | undefined>(undefined);
  const calculRatioHandle = useCallback(() => {
    if (!containerRef.current) return;

    const isTablet = matchMedia(`(min-width: ${DESKTOP_TABLET_MIN}px)`).matches;
    const isDesktop = matchMedia(`(min-width: ${DESKTOP_MIN}px)`).matches;

    if (!(isDesktop || isTablet)) {
      setRatio(undefined);
      return;
    }

    const textHeight = isDesktop ? TEXT_HEIGHT_DESKTOP : TEXT_HEIGHT_TABLET;
    const computedStyle = window.getComputedStyle(containerRef.current);
    const gapWidthValue = Math.abs(parseFloat(computedStyle.marginLeft));
    const gapHeightValue = Math.abs(parseFloat(computedStyle.marginTop));
    const gridColumns = computedStyle.gridTemplateColumns;
    const cardDimensionWithMargin = parseFloat(gridColumns.split(' ')[0]);
    const cardWidth = cardDimensionWithMargin - gapWidthValue;

    setRatio(calculRatio(cardWidth, gapWidthValue, gapHeightValue, textHeight));
  }, []);

  useEffect(() => {
    calculRatioHandle();

    window.addEventListener('resize', calculRatioHandle);

    return () => window.removeEventListener('resize', calculRatioHandle);
  }, [calculRatioHandle]);

  return ratio;
}

function calculRatio(
  cardWidth: number,
  gapWidth: number,
  gapHeight: number,
  textHeight: number,
  ratioWidth = CATEGORY_IMAGE_PRODUCT_WIDTH,
  ratioHeight = CATEGORY_IMAGE_PRODUCT_HEIGHT
) {
  const cardHeight = (cardWidth * ratioHeight) / ratioWidth + textHeight;
  const bigProductRatioWidth = cardWidth * 2 + gapWidth;
  const bigProductRatioHeight = cardHeight * 2 - textHeight + gapHeight;

  return `${bigProductRatioWidth}/${Math.round(bigProductRatioHeight * 2) / 2}`;
}
